// externals
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { SlideOut } from '@interstate/components/SlideOut';
import { Typography } from '@interstate/components/Typography';
import type { StateTree } from '@makemydeal/dr-dash-types';

// hooks
import { useGetDrawerWidth } from '../dealActivity/activities/hooks/useGetDrawerWidth';

// selectors / actions
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as repairOrderSelectors from '@makemydeal/dr-dash-store/selectors/repairOrderSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';

// components
import RepairOrderCardUI from './RepairOrderCardUI';

// utils
import { formatRepairOrderCount, getRepairOrderKey } from './repairOrderDisplay';

// styles
import { StyledRepairOrderList } from './RepairOrders.style';

export const RepairOrdersSlideOut = () => {
    const dispatch = useDispatch();
    const showRepairOrders = useSelector(baseDealSelectors.getRepairOrdersSlideoutStatus);
    // Read by vin, not by scope: one drawer serves every scenario card, so the scenario whose alert
    // opened it is the only thing that says which car's orders to list.
    const slideoutVin = useSelector(baseDealSelectors.getRepairOrdersSlideoutVin);
    const repairOrders = useSelector((state: StateTree) => repairOrderSelectors.getRepairOrdersByVin(state, slideoutVin));
    const vehicle = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);

    // dc-mv-328: fire once per open, not on every re-render while the drawer stays open.
    const wasShownRef = useRef(false);
    useEffect(() => {
        const isShown = showRepairOrders && repairOrders.length > 0;
        if (isShown && !wasShownRef.current) {
            dispatch(interactionAnalyticsActionCreators.openRepairOrdersSlideOutViewed(vehicle));
        }
        wasShownRef.current = isShown;
    }, [showRepairOrders, repairOrders.length, vehicle, dispatch]);

    const panelWidth = useGetDrawerWidth({
        extraLarge: '40rem',
        large: '40rem',
        tablet: '32.857rem',
        small: '100%',
        extraSmall: '100%'
    });

    const closeRepairOrders = () => {
        dispatch(baseDealActionCreators.setRepairOrdersSlideoutStatus(false));
    };

    if (repairOrders.length === 0) {
        return null;
    }

    return (
        <SlideOut
            data-testid="repair-orders-slide-out"
            id="repair-orders-slide-out"
            show={showRepairOrders}
            position="right"
            panelWidth={panelWidth}
            header={
                <Typography tag="span" variant="h3" color="sem.color.on-surface.default">
                    {formatRepairOrderCount(repairOrders.length)}
                </Typography>
            }
            onHide={closeRepairOrders}
            sx={{ '& .ids-slideout-close-button': { cursor: 'pointer' } }}
        >
            <StyledRepairOrderList data-testid="repair-orders-list">
                {repairOrders.map((repairOrder, index) => (
                    <RepairOrderCardUI key={getRepairOrderKey(repairOrder)} repairOrder={repairOrder} index={index} />
                ))}
            </StyledRepairOrderList>
        </SlideOut>
    );
};

export default RepairOrdersSlideOut;
